import { tOr } from '../../i18n/i18n'
/**
 * Which model answers, and from where.
 *
 * Two providers, chosen by region, for one reason: the OpenAI API is not
 * reachable from every country this app is used in, and the classroom cannot
 * depend on a network path that some students do not have. So US traffic goes
 * to OpenAI and everyone else goes to DeepSeek.
 *
 * The convenient part is that both speak the SAME wire protocol. DeepSeek ships
 * an OpenAI-compatible `/chat/completions` endpoint, so a provider here is just
 * a base URL, a key, and a model id - one request builder covers both, and a
 * third provider later is four lines rather than a new client.
 *
 * MODEL IDS, and the trap in each of them. Both confirmed against the
 * providers' live model lists on 2026-09-10.
 *
 * The OpenAI one is `gpt-5.6-luna`, not `chatgpt-` - the `chatgpt-` prefix
 * belongs to a different family and 404s here.
 *
 * The DeepSeek one is UNVERSIONED BECAUSE DEEPSEEK MADE IT SO, and this is
 * worth reading before anyone "fixes" it to name a version. `deepseek-flash`
 * IS V4.1 Flash - the pricing page maps that id to it - and there is no
 * versioned spelling to write instead: the API rejects `deepseek-v4.1-flash`
 * with an error naming the only two ids it accepts, `deepseek-flash` and
 * `deepseek-v4-pro`.
 *
 * The `deepseek-v4-flash` that stood here before is a RETIRED model whose id
 * is still accepted and quietly served by V4.1 - which is how this file kept
 * working while naming something that no longer ran. `deepseek-v4-pro` is
 * retiring the same way on 2026-09-14, routed to V4.1 Flash, so this one id
 * is the whole DeepSeek line from here.
 *
 * If a call comes back `model_not_found`, this is the first file to check and
 * `functions/talkPrompt.js` is the second - they carry the same two strings.
 */

export type ProviderId = 'openai' | 'deepseek'

/** US or not. Everything downstream reduces to this one bit. */
export type Region = 'us' | 'intl'

export interface ProviderSpec {
  id: ProviderId
  /** Shown in the UI when the student asks who is answering. */
  label: string
  /** The model id sent in the request body. Verify against the provider. */
  model: string
  /** OpenAI-compatible chat-completions base. No trailing slash. */
  baseUrl: string
  /** Name of the secret the proxy reads. Never a client-side variable. */
  keyEnv: string
  /** Where to check the model id and the key. */
  docs: string
}

export const PROVIDERS: Record<ProviderId, ProviderSpec> = {
  openai: {
    id: 'openai',
    label: 'ChatGPT',
    model: 'gpt-5.6-luna',
    baseUrl: 'https://api.openai.com/v1',
    keyEnv: 'OPENAI_API_KEY',
    docs: 'https://platform.openai.com/docs/models',
  },
  deepseek: {
    id: 'deepseek',
    label: tOr('tool.config.LabelDeepseek', 'DeepSeek'),
    model: 'deepseek-flash',
    baseUrl: 'https://api.deepseek.com/v1',
    keyEnv: 'DEEPSEEK_API_KEY',
    docs: 'https://api-docs.deepseek.com/quick_start/pricing',
  },
}

/** The default this whole feature exists to express. */
export const PROVIDER_FOR_REGION: Record<Region, ProviderId> = {
  us: 'openai',
  intl: 'deepseek',
}

export const providerFor = (region: Region): ProviderSpec =>
  PROVIDERS[PROVIDER_FOR_REGION[region]]

/**
 * Where the browser sends its routing request. Not a provider URL: the client
 * never holds a key. See `functions/index.js` for what is on the other end.
 */
export const ROUTER_ENDPOINT = '/api/route'

/**
 * How long to wait before giving up and routing locally.
 *
 * This was 4s, on the reasoning that a student waiting on a spinner is worse
 * served than one whose question was pattern-matched. Measured against the live
 * providers that turned out to be a trap: OpenAI answers in 1.8-4.4s from a
 * warm local process, and a cold Cloud Run instance adds several seconds on top
 * of that. A 4s budget would have expired on a working call and dropped to the
 * pattern router, silently, most often on the first question of a lesson - the
 * one time it is most likely to be watched.
 *
 * 10s costs a slow question its spinner. It does not cost a correct answer.
 */
export const ROUTER_TIMEOUT_MS = 10_000
